import React from "react";
import { Link } from "react-router-dom";
import { Popup } from "semantic-ui-react";
import { Answer, Enrolment } from "../../../../stores/EnrolmentStore/Types";
import { TopicPreview } from "../../../../stores/CoursePreviewStore/Types";
import { CourseOverviewState } from "../types";
import {
  CIRCLE_LENGTH,
  findCurrentLesson,
  questionPath,
  translatedName,
} from "../helpers";

interface Props {
  answers: Answer[];
  chapters: TopicPreview[];
  currentUser: any;
  enrolment: Enrolment;
  onOpenPersonalizationModal: () => void;
  onOpenRepersonalizationModal: () => void;
  personalizationState: string;
  state: CourseOverviewState;
}

const StartCard = ({
  answers,
  chapters,
  currentUser,
  enrolment,
  onOpenPersonalizationModal,
  onOpenRepersonalizationModal,
  personalizationState,
  state,
}: Props) => {
  const courseLocked = !!enrolment?.locked;
  // A missed payment-plan instalment: the person already owns a slice of
  // this course, so the fix is a new card in Settings, not the checkout.
  const planLocked = courseLocked && !!enrolment?.payment_plan_locked;
  const percentage =
    state === "complete" ? 100 : enrolment?.percentage_completed || 0;
  const currentLesson = findCurrentLesson(enrolment, chapters, answers);
  const currentChapter = chapters.find((chapter) =>
    chapter.questions.some((question) => question.id === currentLesson?.id),
  );
  const firstChapter = chapters[0];
  const firstLesson = firstChapter?.questions[0];
  const lesson = currentLesson || firstLesson;
  const chapter = currentChapter || firstChapter;
  const actionPath = planLocked
    ? "/profile?settings=plan"
    : courseLocked
    ? `/courses/${enrolment?.course?.slug}/pay`
    : state === "complete"
      ? `/enrolments/${enrolment?.id}/certificate`
      : lesson
        ? questionPath(enrolment?.id, lesson, answers)
        : `/enrolments/${enrolment?.id}`;
  const title = courseLocked
    ? "Course locked"
    : state === "complete"
      ? "Course complete"
      : state === "fresh"
        ? "First up"
        : "Next up";
  const where = planLocked
    ? "A payment on your plan is due"
    : courseLocked
    ? "Unlock this course to continue"
    : state === "complete"
      ? "All chapters done"
      : chapter && lesson
        ? `Chapter ${chapter.position + 1} · Lesson ${lesson.position + 1}`
        : "Course overview";
  const lessonName = planLocked
    ? "Add a new card in Settings and continue your payment plan. The course unlocks the moment the payment goes through."
    : courseLocked
    ? "Buy the course to access lessons, personalise your learning, and track your progress."
    : state === "complete"
      ? "Nice work. Your certificate is ready."
      : lesson
        ? translatedName(lesson, currentUser?.locale)
        : "Start learning when you're ready.";
  const actionLabel = planLocked
    ? "Continue payment plan"
    : courseLocked
    ? "Buy Course"
    : state === "complete"
      ? "Get Certificate"
      : state === "fresh"
        ? "Get Started"
        : "Continue";
  const generatingSignals = ["generating", "generating_progress"];
  const resolvedPersonalizationState =
    personalizationState || enrolment?.personalization_state;
  const isGenerating = generatingSignals.includes(resolvedPersonalizationState);
  const hasBeenPersonalized = [...generatingSignals, "complete"].includes(
    resolvedPersonalizationState,
  );
  const isPersonalizationPresent =
    enrolment?.can_repersonalize || hasBeenPersonalized;
  const personalizationLabel = isPersonalizationPresent
    ? "Re-personalise"
    : "Personalise";
  const handlePersonalizationClick = isPersonalizationPresent
    ? onOpenRepersonalizationModal
    : onOpenPersonalizationModal;
  const personalizationButton = (
    <span className="startcard-personalization-trigger">
      <button
        className="btn btn-outline"
        type="button"
        onClick={handlePersonalizationClick}
        disabled={isGenerating || courseLocked}
      >
        <span>{personalizationLabel}</span>
        <span aria-hidden="true">&#10024;</span>
      </button>
    </span>
  );

  return (
    <div className="container startcard-wrap">
      <div className="startcard">
        <div className="ring" aria-hidden="true">
          <svg viewBox="0 0 62 62">
            <circle className="ring-bg" cx="31" cy="31" r="26" />
            <circle
              className="ring-fg"
              cx="31"
              cy="31"
              r="26"
              strokeDasharray={CIRCLE_LENGTH}
              strokeDashoffset={
                CIRCLE_LENGTH - (percentage / 100) * CIRCLE_LENGTH
              }
            />
          </svg>
          <span className="ring-num" style={{ display: "flex" }}>
            {percentage}%
          </span>
        </div>
        <div className="meta">
          <span className="kicker">{title}</span>
          <div className="where">{where}</div>
          <div className="lesson-name">{lessonName}</div>
        </div>
        <div className="acts">
          <Link className="btn btn-blue" to={actionPath}>
            {actionLabel}
          </Link>
          {isGenerating ? (
            <Popup
              basic
              className="personalization-progress-popup"
              trigger={personalizationButton}
              style={{
                color: "white",
                backgroundColor: "#392d40",
              }}
            >
              <Popup.Content>Personalisation in progress.</Popup.Content>
            </Popup>
          ) : (
            personalizationButton
          )}
        </div>
        {courseLocked && (
          <div className="startcard__locked" role="status">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 10V8a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h1zm2 0h6V8a3 3 0 0 0-6 0v2z" />
            </svg>
            <span>
              {planLocked
                ? "This course is locked until the missed payment on your plan goes through."
                : "This course is locked. Buy the course to unlock the chapters."}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartCard;
